import { useEventListener, useMountEffect, useResizeListener, useUnmountEffect } from 'primereact/hooks';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import type { AppTopbarRef, ChildContainerProps, LayoutConfig, LayoutState } from 'types/index';
import { classNames, DomHandler } from 'primereact/utils';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { LayoutContext } from './context/layoutcontext';
import { PrimeReactContext } from 'primereact/api';
import { useLocation } from 'react-router-dom';
import { routes } from 'routes/Routes';
import AppSidebar from './AppSidebar';
import AppTopbar from './AppTopbar';
import AppFooter from './AppFooter';

const Layout = (props: ChildContainerProps) => {
    const { layoutConfig, layoutState, setLayoutState, setLayoutConfig, isSlim, isSlimPlus, isHorizontal, isDesktop, isSidebarActive } = useContext(LayoutContext);
    const { setRipple } = useContext(PrimeReactContext);
    const topbarRef = useRef<AppTopbarRef>(null);
    const sidebarRef = useRef<HTMLDivElement>(null);
    const location = useLocation();
    const pathname = location.pathname;
    const searchParams = location.search;
    let timeout: NodeJS.Timeout | null = null;

    const horizontalRoutes = ['/custom/orders/detail', '/custom/orders/v2/detail', '/custom/orders/new'];
    const originalMenuModeRef = useRef(layoutConfig.menuMode);
    const isForcedHorizontal = horizontalRoutes.some((r) => pathname.startsWith(r));


    ///TODO: Esto arregla el error visual del menu PILAS Y CUIDADO SE ELIMINA
    useEffect(() => {
        if (isForcedHorizontal) {
            originalMenuModeRef.current = layoutConfig.menuMode;
            setLayoutConfig(prev => ({ ...prev, menuMode: 'slim-plus' }));

            // Limpiar clases activas si se forzó horizontal
            const activeItems = document.querySelectorAll('.layout-root-menuitem.active-menuitem');

            activeItems.forEach(el => {
                const toggleLink = el.querySelector('a');
                // console.log(activeItems)
                // alert('se ejecuto')
                setTimeout(() => {
                    if (toggleLink) toggleLink.dispatchEvent(new MouseEvent('click', { bubbles: true }));
                    el.classList.remove('active-menuitem');
                }, 20);

            });

        }

        if (!isForcedHorizontal) {
            setLayoutConfig(prev => ({ ...prev, menuMode: originalMenuModeRef.current || 'static' }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    const normalizedPath = pathname.startsWith('/') ? pathname.substring(1) : pathname;
    const isPublicRoute = pathname.startsWith('/customs/pre-liquidation/approve/') 
                        || pathname.startsWith('/customs/inspection/photos/')
                        || pathname.startsWith('/customs/control/photos/')
                        || pathname.startsWith('/customs/orders/') ;
    const currentRoute = routes.find(route => {
        if (pathname === '/' && route.path === '/') return true;
        const routePath = route.path.startsWith('/') ? route.path.substring(1) : route.path;
        return routePath === normalizedPath;
    });
    const state = location.state as { editing?: boolean };
    useDocumentTitle(
        typeof currentRoute?.pathLabel === 'string'
            ? currentRoute.pathLabel
            : currentRoute?.pathLabel?.(state) ?? 'Página no encontrada',
        { appName: 'Diligent' }
    );

    const [bindMenuOutsideClickListener, unbindMenuOutsideClickListener] = useEventListener({
        type: 'click',
        listener: (event) => {
            const isOutsideClicked = !(
                sidebarRef.current?.isSameNode(event.target as Node) ||
                sidebarRef.current?.contains(event.target as Node) ||
                topbarRef.current?.menubutton?.isSameNode(event.target as Node) ||
                topbarRef.current?.menubutton?.contains(event.target as Node)
            );

            if (isOutsideClicked) {
                hideMenu();
            }
        }
    });

    const [bindDocumentResizeListener, unbindDocumentResizeListener] = useResizeListener({
        listener: () => {
            if (isDesktop() && !DomHandler.isTouchDevice()) {
                hideMenu();
            }
        }
    });

    const hideMenu = useCallback(() => {
        setLayoutState((prevLayoutState: LayoutState) => ({
            ...prevLayoutState,
            overlayMenuActive: false,
            overlaySubmenuActive: false,
            staticMenuMobileActive: false,
            menuHoverActive: false,
            resetMenu: (isSlim() || isSlimPlus() || isHorizontal()) && isDesktop()
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSlim, isSlimPlus, isHorizontal, isDesktop]);

    const blockBodyScroll = () => {
        if (document.body.classList) {
            document.body.classList.add('blocked-scroll');
        } else {
            document.body.className += ' blocked-scroll';
        }
    };

    const unblockBodyScroll = () => {
        if (document.body.classList) {
            document.body.classList.remove('blocked-scroll');
        } else {
            document.body.className = document.body.className.replace(new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        }
    };
    useMountEffect(() => {
        setRipple?.(layoutConfig.ripple);
    });

    const onMouseEnter = () => {
        if (!layoutState.anchored) {
            if (timeout) {
                clearTimeout(timeout);
                timeout = null;
            }
            setLayoutState((prevLayoutState: LayoutState) => ({
                ...prevLayoutState,
                sidebarActive: true
            }));
        }
    };

    const onMouseLeave = () => {
        if (!layoutState.anchored) {
            if (!timeout) {
                timeout = setTimeout(
                    () =>
                        setLayoutState((prevLayoutState: LayoutState) => ({
                            ...prevLayoutState,
                            sidebarActive: false
                        })),
                    300
                );
            }
        }
    };

    useEffect(() => {
        const onRouteChange = () => {
            if (layoutConfig.colorScheme === 'dark') {
                setLayoutConfig((prevState: LayoutConfig) => ({ ...prevState, menuTheme: 'dark' }));
            }
        };
        onRouteChange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, searchParams]);

    useEffect(() => {
        if (isSidebarActive()) {
            bindMenuOutsideClickListener();
        }

        if (layoutState.staticMenuMobileActive) {
            blockBodyScroll();
            if (isSlim() || isSlimPlus() || isHorizontal()) {
                bindDocumentResizeListener();
            }
        }

        return () => {
            unbindMenuOutsideClickListener();
            unbindDocumentResizeListener();
            unblockBodyScroll();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layoutState.overlayMenuActive, layoutState.staticMenuMobileActive, layoutState.overlaySubmenuActive]);

    useEffect(() => {
        const onRouteChange = () => {
            hideMenu();
        };
        onRouteChange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, searchParams]);

    useUnmountEffect(() => {
        unbindMenuOutsideClickListener();
    });

    const containerClassName = classNames('layout-topbar-' + layoutConfig.topbarTheme, 'layout-menu-' + layoutConfig.menuTheme, 'layout-menu-profile-' + layoutConfig.menuProfilePosition, {
        'layout-overlay': layoutConfig.menuMode === 'overlay',
        'layout-static': !isPublicRoute && layoutConfig.menuMode === 'static',
        'layout-slim': layoutConfig.menuMode === 'slim',
        'layout-slim-plus': layoutConfig.menuMode === 'slim-plus',
        'layout-horizontal': layoutConfig.menuMode === 'horizontal',
        'layout-reveal': layoutConfig.menuMode === 'reveal',
        'layout-drawer': layoutConfig.menuMode === 'drawer',
        'p-input-filled': layoutConfig.inputStyle === 'filled',
        'layout-sidebar-dark': layoutConfig.colorScheme === 'dark',
        'p-ripple-disabled': !layoutConfig.ripple,
        'layout-static-inactive': (layoutState.staticMenuDesktopInactive && layoutConfig.menuMode === 'static'),
        'layout-public-route': isPublicRoute,
        'layout-overlay-active': layoutState.overlayMenuActive,
        'layout-mobile-active': layoutState.staticMenuMobileActive,
        'layout-topbar-menu-active': layoutState.topbarMenuActive,
        'layout-menu-profile-active': layoutState.menuProfileActive,
        'layout-sidebar-active': layoutState.sidebarActive,
        'layout-sidebar-anchored': layoutState.anchored
    });

    return (
        <div className={classNames('layout-container', containerClassName)}>
            <AppTopbar ref={topbarRef} />
            {!isPublicRoute && (
                <div ref={sidebarRef} className="layout-sidebar" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
                    <AppSidebar />
                </div>
            )}

            <div className="layout-content-wrapper">
                {props.children}
                <AppFooter></AppFooter>
            </div>
            <div className="layout-mask"></div>
        </div>
    );
};

export default Layout;
